import { b2cPolicies } from "./policies";
import { PublicClientApplication, LogLevel, InteractionRequiredAuthError } from "@azure/msal-browser";

// Load and destructure VITE environment variables
const {
  VITE_CLIENT_ID,
  VITE_AUTHORITY,
  VITE_API_SCOPES = "",
} = import.meta.env;

const redirectUri = window.location.origin;
const postLogoutRedirectUri = window.location.origin;

// Detect slot name based on hostname
const host = window.location.hostname;
const slotMap = {
  stage: "stage",
  mobile: "mobile",
  dev: "dev",
  test: "test",
  localhost: "local",
};

const matchedSlot = Object.entries(slotMap).find(([key]) => host.includes(key));
const slot = matchedSlot ? matchedSlot[1] : "production";

console.log("Detected deployment slot:", slot);

// Log environment variable values
console.groupCollapsed("MSAL Environment Variables");
console.log("VITE_CLIENT_ID:", VITE_CLIENT_ID);
if (VITE_CLIENT_ID?.includes("$(")) console.warn("VITE_CLIENT_ID appears unresolved.");
console.log("VITE_AUTHORITY:", VITE_AUTHORITY);
if (VITE_AUTHORITY?.includes("$(")) console.warn("VITE_AUTHORITY appears unresolved.");
console.log("VITE_API_SCOPES:", VITE_API_SCOPES);
if (VITE_API_SCOPES?.includes("$(")) console.warn("VITE_API_SCOPES appears unresolved.");
console.log("redirectUri:", redirectUri);
console.log("postLogoutRedirectUri:", postLogoutRedirectUri);
console.groupEnd();

// Validate required MSAL environment variables
const logMissingEnv = (name) => {
  console.error(`Missing required MSAL environment variable: ${name}. Please check your .env configuration.`);
};

if (!VITE_CLIENT_ID) logMissingEnv("VITE_CLIENT_ID");
if (!VITE_AUTHORITY) logMissingEnv("VITE_AUTHORITY");

// MSAL configuration object
export const msalConfig = {
  auth: {
    clientId: VITE_CLIENT_ID,
    authority: VITE_AUTHORITY,
    knownAuthorities: ["seiu1021b2c.b2clogin.com"],
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        const log = {
          [LogLevel.Error]: console.error,
          [LogLevel.Info]: console.info,
          [LogLevel.Verbose]: console.debug,
          [LogLevel.Warning]: console.warn,
        }[level];
        log?.(message);
      },
      piiLoggingEnabled: false,
      logLevel: LogLevel.Verbose,
    },
  },
};

// Create and initialize the MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);
let msalInitialized = false;

export const initializeMsal = async () => {
  if (!msalInitialized) {
    await msalInstance.initialize();

    // Wait for redirect response to resolve before initiating new interaction
    let redirectResult = null;
    redirectResult = await msalInstance.handleRedirectPromise().catch(async (error) => {
      if (error?.errorMessage?.includes("AADB2C90118")) {
        console.warn("Forgot password triggered. Redirecting to password reset policy.");
        return msalInstance.loginRedirect({
          authority: b2cPolicies.authorities.forgotPassword.authority,
          scopes: ["openid"],
        });
      } else if (error?.errorMessage?.includes("AADB2C90091")) {
        console.warn("Password reset canceled. Redirecting back to sign-in policy.");
        return msalInstance.loginRedirect({
          ...loginRequest,
          authority: b2cPolicies.authorities.signUpSignIn.authority,
        });
      } else {
        console.error("Unhandled error during redirect handling:", error);
        throw error;
      }
    });

    if (window.location.hash.includes("code=") || window.location.hash.includes("state=")) {
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log("Removed URL hash state/code for cleanliness.");
    }

    if (redirectResult) {
      console.log("Login completed via redirect:", redirectResult.account);
      msalInstance.setActiveAccount(redirectResult.account);
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      console.log("MSAL active account set to:", accounts[0]);
    } else {
      console.warn("No MSAL accounts found after redirect. Initiating loginRedirect...");
      try {
        await msalInstance.loginRedirect(loginRequest);
      } catch (e) {
        if (e.errorCode === "interaction_in_progress") {
          console.warn("Interaction already in progress.");
        } else {
          console.error("loginRedirect error:", e);
        }
      }
    }

    msalInitialized = true;
  }
};

/**
 * Returns the initialized MSAL PublicClientApplication instance.
 * Useful for accessing MSAL APIs directly.
 */
export const getMsalInstance = () => msalInstance;

// Prepare scopes
const scopesArray = VITE_API_SCOPES.split(",").map((s) => s.trim()).filter(Boolean);
if (scopesArray.length === 0) {
  console.warn("No API scopes defined in VITE_API_SCOPES.");
}
export const loginRequest = { scopes: scopesArray };
export const tokenRequest = { scopes: scopesArray };

// Acquire access token
export const acquireToken = async () => {
  const instance = getMsalInstance();
  let account = instance.getActiveAccount();

  if (!account) {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      account = accounts[0];
    } else {
      console.warn("No active account found. User may not be logged in.");
      return null;
    }
  }

  try {
    const response = await instance.acquireTokenSilent({ ...tokenRequest, account });
    if (response?.accessToken) return response.accessToken;

    console.warn("No access token received in silent response.");
    return null;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const popupResponse = await instance.acquireTokenPopup({ ...tokenRequest, account });
        if (!popupResponse?.accessToken) {
          console.warn("No access token received in popup response.");
          return null;
        }
        return popupResponse.accessToken;
      } catch (popupError) {
        console.error("Popup token error:", popupError);
        throw popupError;
      }
    } else {
      console.error("Silent token error:", error);
      throw error;
    }
  }
};

// Export deployment slot for use in other parts of the app
export const deploymentSlot = slot;